import { useMemo } from 'react';
import { LatLngTuple } from 'leaflet';
import { Workorder } from '@certhon/models';
import {
  WorkorderMapBase,
  WorkorderMarker,
  HQMarker,
  RoutePolyline,
  useMapBounds,
  HQ_LOCATION,
} from '../../shared';
import { RouteResponse, isRouteResult } from '../dayplanning.types';

interface DayPlanningMapProps {
  workorders: Workorder[];
  routes: RouteResponse[] | undefined;
  /** Geocoded coordinates as fallback when route-derived coords are unavailable */
  geocodedCoords?: Map<number, LatLngTuple>;
  className?: string;
  multiTechnician?: boolean;
  technicianColors?: Map<number, string>;
  selectedTechnicianId?: number | null;
}

/**
 * Route summary for screen readers (visually hidden)
 */
function RouteSummary({
  workorders,
  routes,
}: {
  workorders: Workorder[];
  routes: RouteResponse[] | undefined;
}) {
  const successfulRoutes = routes?.filter(isRouteResult) || [];
  const totalDistance = successfulRoutes.reduce(
    (sum, r) => sum + (r.distance_km || 0),
    0,
  );
  const totalDuration = successfulRoutes.reduce(
    (sum, r) => sum + (r.duration_minutes || 0),
    0,
  );

  return (
    <div className="sr-only" aria-live="polite">
      Route map showing {workorders.length} workorder
      {workorders.length !== 1 ? 's' : ''}.
      {successfulRoutes.length > 0 && (
        <span>
          {' '}
          Total distance: {totalDistance.toFixed(1)} kilometers. Total duration:{' '}
          {Math.round(totalDuration)} minutes.
        </span>
      )}
    </div>
  );
}

export function DayPlanningMap({
  workorders,
  routes,
  geocodedCoords,
  className = '',
  multiTechnician = false,
  technicianColors,
  selectedTechnicianId,
}: DayPlanningMapProps) {
  // Extract workorder coordinates from route endpoints, with geocoded fallback
  // Each route's last geometry point is the destination (workorder location)
  // Note: via_hq creates 2 routes per workorder, so we track route index separately
  // In multi-technician mode, each tech has their own route sequence including trailing route
  const workorderCoords = useMemo(() => {
    const coords = new Map<number, LatLngTuple>();

    // First pass: extract coordinates from routes (only successful ones)
    if (routes && routes.length > 0) {
      let routeIdx = 0;
      let prevTechId: number | null = null;

      workorders.forEach((wo, woIdx) => {
        if (routeIdx >= routes.length) return;

        const techId = wo.assigned_user_id ?? -1;
        const isNewTech =
          multiTechnician && prevTechId !== null && techId !== prevTechId;

        // In multi-tech mode, skip previous tech's trailing route when switching techs
        if (isNewTech) {
          routeIdx++;
          if (routeIdx >= routes.length) return;
        }

        const isFirstForTech = woIdx === 0 || isNewTech;

        if (isFirstForTech) {
          // First workorder for this tech: single route from HQ
          const route = routes[routeIdx];
          if (
            isRouteResult(route) &&
            route.geometry &&
            route.geometry.length > 0
          ) {
            const lastPoint = route.geometry[route.geometry.length - 1];
            coords.set(wo.recnum, [lastPoint[1], lastPoint[0]]);
          }
          routeIdx++;
        } else {
          // Check if via_hq - if so, there are 2 routes (prev->HQ, HQ->WO)
          if (wo.route_option === 'via_hq') {
            routeIdx++; // Skip the "prev -> HQ" route
            if (routeIdx >= routes.length) return;
            const route = routes[routeIdx]; // This is "HQ -> this WO"
            if (
              isRouteResult(route) &&
              route.geometry &&
              route.geometry.length > 0
            ) {
              const lastPoint = route.geometry[route.geometry.length - 1];
              coords.set(wo.recnum, [lastPoint[1], lastPoint[0]]);
            }
            routeIdx++;
          } else {
            const route = routes[routeIdx];
            if (
              isRouteResult(route) &&
              route.geometry &&
              route.geometry.length > 0
            ) {
              const lastPoint = route.geometry[route.geometry.length - 1];
              coords.set(wo.recnum, [lastPoint[1], lastPoint[0]]);
            }
            routeIdx++;
          }
        }

        prevTechId = techId;
      });
    }

    // Second pass: fill in missing coordinates from geocoded data
    if (geocodedCoords) {
      workorders.forEach((wo) => {
        if (!coords.has(wo.recnum)) {
          const geocoded = geocodedCoords.get(wo.recnum);
          if (geocoded) {
            coords.set(wo.recnum, geocoded);
          }
        }
      });
    }

    return coords;
  }, [routes, workorders, geocodedCoords]);

  // Calculate bounds including HQ as fallback
  const bounds = useMapBounds(workorderCoords, {
    fallbackBounds: [
      [HQ_LOCATION.coords[0] - 0.1, HQ_LOCATION.coords[1] - 0.1],
      [HQ_LOCATION.coords[0] + 0.1, HQ_LOCATION.coords[1] + 0.1],
    ],
  });

  // Convert route geometries from [lng, lat] to [lat, lng] for Leaflet
  // Filter out null routes (failed geocoding)
  // In multi-tech mode, associate each route with its technician color
  // In single-tech mode, use the selected technician's color
  // In multi-tech mode, each tech has their own route sequence including trailing route
  const polylines = useMemo(() => {
    if (!routes) return [];

    let routeIdx = 0;
    let prevTechId: number | null = null;
    const polylinesWithColors: Array<{
      positions: LatLngTuple[];
      color?: string;
    }> = [];

    // Determine color based on mode
    // Multi-tech: use per-workorder technician color
    // Single-tech: use selected technician's color
    const getRouteColor = (wo: Workorder) => {
      if (multiTechnician && wo.assigned_user_id) {
        return technicianColors?.get(wo.assigned_user_id);
      } else if (!multiTechnician && selectedTechnicianId) {
        return technicianColors?.get(selectedTechnicianId);
      }
      return undefined;
    };

    workorders.forEach((wo, woIdx) => {
      if (routeIdx >= routes.length) return;

      const techId = wo.assigned_user_id ?? -1;
      const isNewTech =
        multiTechnician && prevTechId !== null && techId !== prevTechId;
      const techColor = getRouteColor(wo);

      // In multi-tech mode, add previous tech's trailing route before switching
      if (isNewTech && routeIdx < routes.length) {
        const prevWo = workorders[woIdx - 1];
        const prevTechColor = prevWo ? getRouteColor(prevWo) : undefined;
        const trailingRoute = routes[routeIdx];

        if (isRouteResult(trailingRoute) && trailingRoute.geometry) {
          polylinesWithColors.push({
            positions: trailingRoute.geometry.map(
              ([lng, lat]) => [lat, lng] as LatLngTuple,
            ),
            color: prevTechColor,
          });
        }
        routeIdx++;
        if (routeIdx >= routes.length) return;
      }

      const isFirstForTech = woIdx === 0 || isNewTech;

      if (isFirstForTech) {
        // First workorder for this tech: single route from HQ
        const route = routes[routeIdx];
        if (isRouteResult(route) && route.geometry) {
          polylinesWithColors.push({
            positions: route.geometry.map(
              ([lng, lat]) => [lat, lng] as LatLngTuple,
            ),
            color: techColor,
          });
        }
        routeIdx++;
      } else {
        // Check if via_hq - if so, there are 2 routes (prev->HQ, HQ->WO)
        if (wo.route_option === 'via_hq') {
          const route1 = routes[routeIdx];
          if (isRouteResult(route1) && route1.geometry) {
            polylinesWithColors.push({
              positions: route1.geometry.map(
                ([lng, lat]) => [lat, lng] as LatLngTuple,
              ),
              color: techColor,
            });
          }
          routeIdx++;

          const route2 = routes[routeIdx];
          if (isRouteResult(route2) && route2.geometry) {
            polylinesWithColors.push({
              positions: route2.geometry.map(
                ([lng, lat]) => [lat, lng] as LatLngTuple,
              ),
              color: techColor,
            });
          }
          routeIdx++;
        } else {
          const route = routes[routeIdx];
          if (isRouteResult(route) && route.geometry) {
            polylinesWithColors.push({
              positions: route.geometry.map(
                ([lng, lat]) => [lat, lng] as LatLngTuple,
              ),
              color: techColor,
            });
          }
          routeIdx++;
        }
      }

      prevTechId = techId;
    });

    // Add trailing route for the last technician
    if (routeIdx < routes.length && workorders.length > 0) {
      const trailingRoute = routes[routeIdx];
      const lastWo = workorders[workorders.length - 1];
      const techColor = getRouteColor(lastWo);

      if (isRouteResult(trailingRoute) && trailingRoute.geometry) {
        polylinesWithColors.push({
          positions: trailingRoute.geometry.map(
            ([lng, lat]) => [lat, lng] as LatLngTuple,
          ),
          color: techColor,
        });
      }
    }

    return polylinesWithColors;
  }, [
    routes,
    workorders,
    multiTechnician,
    technicianColors,
    selectedTechnicianId,
  ]);

  // Calculate per-technician index for each workorder
  const workorderIndices = useMemo(() => {
    const indices = new Map<number, number>();

    if (multiTechnician) {
      // Track index per technician
      const techIndices = new Map<number, number>();

      workorders.forEach((wo) => {
        const techId = wo.assigned_user_id ?? -1; // Use -1 for unassigned
        const currentIndex = techIndices.get(techId) ?? 0;
        indices.set(wo.recnum, currentIndex + 1);
        techIndices.set(techId, currentIndex + 1);
      });
    } else {
      // Global index
      workorders.forEach((wo, idx) => {
        indices.set(wo.recnum, idx + 1);
      });
    }

    return indices;
  }, [workorders, multiTechnician]);

  return (
    <WorkorderMapBase bounds={bounds} className={className}>
      <RouteSummary workorders={workorders} routes={routes} />
      {/* HQ Marker */}
      <HQMarker position={HQ_LOCATION.coords} address={HQ_LOCATION.address} />

      {/* Workorder markers */}
      {workorders.map((wo) => {
        const coords = workorderCoords.get(wo.recnum);
        if (!coords) return null;

        // Determine marker color based on mode
        const techColor = (() => {
          if (multiTechnician && wo.assigned_user_id) {
            return technicianColors?.get(wo.assigned_user_id);
          } else if (!multiTechnician && selectedTechnicianId) {
            return technicianColors?.get(selectedTechnicianId);
          }
          return undefined;
        })();

        const markerIndex = workorderIndices.get(wo.recnum) ?? 1;

        return (
          <WorkorderMarker
            key={wo.recnum}
            workorder={wo}
            position={coords}
            index={markerIndex}
            color={techColor}
          />
        );
      })}

      {/* Route polylines */}
      {polylines.map((polyline, idx) => (
        <RoutePolyline
          key={idx}
          positions={polyline.positions}
          color={polyline.color}
        />
      ))}
    </WorkorderMapBase>
  );
}
